import dayjs, { OpUnitType } from 'dayjs';
import { ReactElement, memo, useEffect, useState } from 'react';
import { Event, EventProps, View, Views, ViewsProps } from 'react-big-calendar';
import BaseCalendar, { BaseCalendarProps } from '~/components/common/base-calendar/BaseCalendar';
import styles from './Calendar.module.scss';
import classNames from 'classnames/bind';
import { EVENT_COLOR_DEFAULT, SCHEDULE_MODE_MYSELF } from '~/utils/constants/schedules';
import { Avatar, Box } from '@mui/material';
import { BaseScrollbar } from '~/components/common';
import {
  IScheduleDayEvent,
  IScheduleDayEventGroup,
  IScheduleEvent,
  IScheduleEventAssignee,
} from '~/utils/interface/schedule';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ScheduleMode } from '~/utils/types/schedule';
import BaseCalendarDay, {
  BaseCalendarDayGroup,
  BaseCalendarDayItem,
} from '~/components/common/base-calendar-day/BaseCalendarDay';
import { stringToColor } from '~/utils/helper';

dayjs.extend(customParseFormat);

type CalendarProps = Omit<BaseCalendarProps, 'view' | 'date' | 'onSelectEvent' | 'events'> & {
  view: View;
  date: string;
  events: Array<IScheduleEvent>;
  dayEvents: Array<IScheduleDayEventGroup>;
  onSelectEvent: (event: IScheduleEvent) => void;
  scheduleMode?: ScheduleMode;
};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const calendarTypes: { [field: string]: string } = {
  single: 'single',
  multiple: 'multiple',
};

const renderAssigneesEventComponent = (assignees: Array<IScheduleEventAssignee> | null, eventColor: string) => {
  if (!assignees) return null;
  const numberOfMoreAssignees: number = assignees.length > 3 ? assignees.length - 3 : 0;
  const displayedAssignees: Array<IScheduleEventAssignee> = assignees.slice(0, 3);

  return (
    <Box className={cx('event-assignees-container')}>
      {displayedAssignees.map((assignee: IScheduleEventAssignee, index: number) => {
        return (
          <Box key={index} className='event-assignee-container'>
            <Box
              className={`event-assignee border-[2px]`}
              sx={{
                borderColor: eventColor,
              }}
            >
              {assignee.avatar ? (
                <Avatar sx={{ height: '100%', width: '100%' }} alt={assignee?.name} src={assignee.avatar} />
              ) : (
                <Avatar
                  sx={{ bgcolor: stringToColor(assignee?.name), height: '100%', width: '100%', fontSize: '12px' }}
                >
                  {assignee?.name[0]}
                </Avatar>
              )}
            </Box>
          </Box>
        );
      })}
      {numberOfMoreAssignees > 0 && (
        <Box className='event-assignee-container'>
          <Box
            className={`event-assignee event-assignee-more border-[2px]`}
            sx={{
              borderColor: eventColor,
              backgroundColor: eventColor,
            }}
          >
            +{numberOfMoreAssignees}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SingleEventView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor = event.color || EVENT_COLOR_DEFAULT;

  return (
    <Box
      className={`event-container event-container-detailed-type`}
      sx={{ borderWidth: 1, borderColor: eventColor, backgroundColor: eventColor }}
    >
      <Box className='text-ellipsis overflow-hidden'>{event.name}</Box>
      <Box className={cx('event-time')}>
        {dayjs(event.startTime, 'HH:mm:ssZ').format('hh:mm a')} - {dayjs(event.endTime, 'HH:mm:ssZ').format('hh:mm a')}
      </Box>
      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const MultipleEventView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor = event.color || EVENT_COLOR_DEFAULT;
  return (
    <Box
      className={`event-container event-container-title-type`}
      sx={{ borderWidth: 1, borderColor: eventColor, backgroundColor: eventColor }}
    >
      <Box className='text-ellipsis overflow-hidden'>{event.name}</Box>
    </Box>
  );
});

const HorizontalSingleEventView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.color || EVENT_COLOR_DEFAULT;

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col flex-wrap justify-between`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
      }}
    >
      <Box className='flex flex-col flex-1'>
        <Box className='truncate'>{event.name}</Box>
        <Box className={cx('truncate event-time')}>
          {dayjs(event.startTime, 'HH:mm:ssZ').format('hh:mm a')} -{' '}
          {dayjs(event.endTime, 'HH:mm:ssZ').format('hh:mm a')}
        </Box>
      </Box>
      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const VerticalMultipleEventView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.color || EVENT_COLOR_DEFAULT;

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-wrap items-center justify-between`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
      }}
    >
      <Box className='truncate'>{event.name}</Box>
      <Box className={cx('truncate event-time')}>
        {dayjs(event.startTime, 'HH:mm:ssZ').format('hh:mm a')} - {dayjs(event.endTime, 'HH:mm:ssZ').format('hh:mm a')}
      </Box>
      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const VerticalSingleEventView = memo((props: { event: IScheduleEvent }) => {
  const { event } = props;
  const eventColor: string = event.color || EVENT_COLOR_DEFAULT;

  return (
    <Box
      className={cx(`event-container event-container-detailed-type flex flex-col flex-wrap justify-between`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
      }}
    >
      <Box className='flex flex-col flex-1'>
        <Box className='truncate'>{event.name}</Box>
        <Box className={cx('truncate event-time')}>
          {dayjs(event.startTime, 'HH:mm:ssZ').format('hh:mm a')} -{' '}
          {dayjs(event.endTime, 'HH:mm:ssZ').format('hh:mm a')}
        </Box>
      </Box>
      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const DayLeftColumn = memo((props: BaseCalendarDayGroup<IScheduleDayEventGroup>) => {
  const name = props.resource?.name ?? 'Unknown';
  return (
    <Box className='flex items-center gap-[10px] h-[100px] px-[10px]'>
      {props.resource?.avatar ? (
        <Avatar alt={props.resource?.name} src={props.resource?.avatar} />
      ) : (
        <Avatar sx={{ bgcolor: stringToColor(name) }}>{name[0]}</Avatar>
      )}
      <Box className='truncate'>{name}</Box>
    </Box>
  );
});

const Calendar = (props: CalendarProps): ReactElement => {
  //#region Destructuring Props
  const { view, date, events, ref, onSelectEvent, ...restProps } = props;
  const currentHour = dayjs().hour();
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [calendarEvents, setCalendarEvents] = useState<Array<Event>>([]);
  const [eventGroups, setEventGroups] = useState<Array<BaseCalendarDayGroup<IScheduleDayEventGroup>>>([]);
  const [eventItems, setEventItems] = useState<Array<BaseCalendarDayItem<IScheduleDayEvent>>>([]);
  const [calendarType, setCalendarType] = useState<string>(calendarTypes.muliple);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.view === Views.MONTH || props?.scheduleMode === SCHEDULE_MODE_MYSELF) {
      setCalendarType(calendarTypes.single);
    }

    if ((props.view === Views.WEEK || props.view === Views.DAY) && props?.scheduleMode !== SCHEDULE_MODE_MYSELF) {
      setCalendarType(calendarTypes.multiple);
    }
  }, [props.view, props?.scheduleMode]);

  useEffect(() => {
    if (calendarType === calendarTypes.single) {
      if (props.view === Views.MONTH) {
        setCalendarEvents(prepareMonthEventsData(props.events));
      } else if (props.view === Views.DAY || props.view === Views.WEEK) {
        setCalendarEvents(prepareDayEventsData(props.events));
      }
    }
  }, [calendarType, props.events, props.view]);

  useEffect(() => {
    if (calendarType === calendarTypes.multiple) {
      prepareDayEventGroup(props.dayEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarType, props.dayEvents]);
  //#endregion Implement Hook

  //#region Handle Function
  const prepareMonthEventsData = (events: Array<IScheduleEvent>): Array<Event> => {
    const calendarEvents: Array<Event> = [];
    for (let event of events) {
      let monthEvent = calendarEvents.find((monthEvent: Event) => {
        return dayjs(monthEvent.start).isSame(dayjs(event.startAt), 'date');
      });

      if (!monthEvent) {
        monthEvent = {
          start: dayjs(event.startAt).toDate(),
          end: dayjs(event.startAt).endOf('date').toDate(),
          resource: [],
        };
        calendarEvents.push(monthEvent);
      }

      monthEvent.resource.push(event);
    }
    return calendarEvents;
  };

  const prepareDayEventsData = (events: Array<IScheduleEvent>): Array<Event> => {
    return events.map((event) => {
      const startTime = dayjs(event.startTime, 'HH:mm:ssZ');
      const endTime = dayjs(event.endTime, 'HH:mm:ssZ');
      const startAt = dayjs(event.startAt).hour(startTime.hour()).minute(startTime.minute());
      const endAt = dayjs(event.startAt).hour(endTime.hour()).minute(endTime.minute());
      return {
        start: startAt.toDate(),
        end: endAt.toDate(),
        resource: [event],
      };
    });
  };

  const prepareDayEventGroup = (itemEvents: Array<IScheduleDayEventGroup>) => {
    const groups: Array<BaseCalendarDayGroup<IScheduleDayEventGroup>> = [];
    let items: Array<BaseCalendarDayItem<IScheduleDayEvent>> = [];

    itemEvents.forEach((itemEvent) => {
      groups.push({
        id: itemEvent.id,
        title: '',
        resource: itemEvent,
      });

      itemEvent.events.forEach((item: IScheduleDayEvent) => {
        const extItem = {
          ...item,
          assignees: itemEvents
            .filter((itemEvent) => itemEvent.events.find((assigneeEvent) => assigneeEvent.id === item.id))
            .map((assignee) => ({
              id: assignee.id,
              name: assignee.name,
              avatar: assignee.avatar,
            })),
        };

        const startTime = dayjs(item.startTime, 'HH:mm:ssZ');
        const startAt = dayjs(item.startAt).hour(startTime.hour()).minute(startTime.minute());
        const endAt = dayjs(item.startAt).hour(startTime.hour()).minute(startTime.minute());
        let dateFormat = (view === Views.DAY ? 'hour' : 'day') as OpUnitType;

        const eventObj: BaseCalendarDayItem<IScheduleDayEvent> = {
          id: `${itemEvent.id}-${item.id}`,
          group: itemEvent.id,
          start_time: startAt.startOf(dateFormat).toDate(),
          end_time: endAt.endOf(dateFormat).toDate(),
          resource: [],
        };

        const index = items.findIndex(({ start_time, group }) => {
          return startAt.isSame(start_time, dateFormat) && itemEvent.id === group;
        });

        // Existing
        if (index > -1) {
          return items[index].resource.push(extItem);
        }

        eventObj.resource.push(extItem);
        items.push(eventObj);
      });
    });

    setEventGroups(groups);
    setEventItems(items);
  };
  //#endregion Handle Function

  return (
    <Box className={cx('calendar-component')}>
      {calendarType === calendarTypes.single && (
        <Box
          className={cx(
            `hour-${currentHour + 1}`,
            { 'h-[735px]': view === Views.MONTH },
            { 'calendar-day-view': view === Views.DAY },
            { 'calendar-week-view': view === Views.WEEK }
          )}
        >
          <BaseCalendar
            events={calendarEvents}
            selectable={true}
            toolbar={false}
            date={date}
            views={views}
            view={view}
            components={{
              event: ({ event }: EventProps) => {
                if (view === Views.MONTH) {
                  return (
                    <BaseScrollbar maxHeight='95px'>
                      <Box className='flex flex-col gap-[4px]'>
                        {event.resource.map((eventData: IScheduleEvent, index: number) => {
                          return (
                            <Box key={index} onClick={() => onSelectEvent(eventData)}>
                              {event.resource.length > 1 ? (
                                <MultipleEventView event={eventData} />
                              ) : (
                                <SingleEventView event={eventData} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </BaseScrollbar>
                  );
                } else if (view === Views.DAY) {
                  const eventData = event.resource[0] as IScheduleEvent;
                  const duration = dayjs(event.end).diff(dayjs(event.start), 'minutes');
                  return (
                    <Box className={cx(`flex size-full`)} onClick={() => onSelectEvent(eventData)}>
                      {duration > 60 ? (
                        <VerticalSingleEventView event={eventData} />
                      ) : (
                        <VerticalMultipleEventView event={eventData} />
                      )}
                    </Box>
                  );
                } else if (view === Views.WEEK) {
                  const eventData = event.resource[0] as IScheduleEvent;
                  const duration = dayjs(event.end).diff(dayjs(event.start), 'minutes');
                  return (
                    <Box className={cx(`flex size-full`)} onClick={() => onSelectEvent(eventData)}>
                      {duration > 60 ? (
                        <VerticalSingleEventView event={eventData} />
                      ) : (
                        <VerticalMultipleEventView event={eventData} />
                      )}
                    </Box>
                  );
                }
              },
            }}
            onSelectEvent={(event, e) => {
              e.preventDefault();
            }}
            {...restProps}
          />
        </Box>
      )}

      {calendarType === calendarTypes.multiple && (
        <Box className={cx({ 'min-w-[3600px]': view === Views.DAY })}>
          <BaseCalendarDay<IScheduleDayEventGroup, IScheduleDayEvent>
            date={date}
            groups={eventGroups}
            items={eventItems}
            groupRenderer={(group) => <DayLeftColumn {...group} />}
            mode={view as OpUnitType}
            itemRenderer={(event) => {
              if (view === Views.WEEK) {
                return (
                  <BaseScrollbar maxHeight='88px'>
                    <Box className='flex flex-col gap-[4px] calendar-week-multiple-view'>
                      {event.resource.map((eventData: IScheduleDayEvent, index: number) => {
                        return (
                          <Box key={index} onClick={() => onSelectEvent(eventData)}>
                            {event.resource.length > 1 ? (
                              <MultipleEventView event={eventData} />
                            ) : (
                              <SingleEventView event={eventData} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </BaseScrollbar>
                );
              }

              return (
                <BaseScrollbar maxHeight='88px'>
                  <Box className='flex flex-col gap-[4px] calendar-week-multiple-view'>
                    {event.resource.map((eventData: IScheduleDayEvent, index: number) => {
                      return (
                        <Box key={index} onClick={() => onSelectEvent(eventData)}>
                          {event.resource.length > 1 ? (
                            <MultipleEventView event={eventData} />
                          ) : (
                            <HorizontalSingleEventView event={eventData} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </BaseScrollbar>
              );
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(Calendar);
